import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../utils/AuthContext';
import API from '../utils/API';
import AdminAwards from './AdminAwards';
import AdminCreateProfile from './AdminCreateProfile';
import AdminIssueAreas from './AdminIssueAreas';
import AdminHearings from './AdminHearings';
import AdminLegislativeTransition from './AdminLegislativeTransition';
import AdminApprovedDomains from './AdminApprovedDomains';
import AdminApprovedEmails from './AdminApprovedEmails';
import AdminAmbassadors from './AdminAmbassadors';
import EditOrganizationModal from '../components/EditOrganizationModal';
import AdminCreateOrgModal from '../components/AdminCreateOrgModal';
import PageTitle from '../components/PageTitle';
import './admin.css';


export default function Admin() {
  const { isAdmin, auth, refreshStatus } = useContext(AuthContext);
  const navigate = useNavigate();

  const [userSearch, setUserSearch] = useState('');
  const [letterFilter, setLetterFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [usersSubTab, setUsersSubTab] = useState('registered');
  const [saving, setSaving] = useState({});
  const [userSort, setUserSort] = useState({ field: 'last_active', dir: 'desc' });
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [userActivity, setUserActivity] = useState(null);
  const [teamDetail, setTeamDetail] = useState(null);
  const [loadingTeamDetail, setLoadingTeamDetail] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgResults, setOrgResults] = useState([]);
  const [orgSearching, setOrgSearching] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [addingMember, setAddingMember] = useState('');
  const [removingMember, setRemovingMember] = useState('');
  const [trialPrompt, setTrialPrompt] = useState(null);
  const [ambassadorMonthly, setAmbassadorMonthly] = useState('');
  const [ambassadorAnnual, setAmbassadorAnnual] = useState('');
  const [savingAmbassador, setSavingAmbassador] = useState(false);
  const orgSearchTimer = useRef(null);
  const memberSearchTimer = useRef(null);

  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizationGroups, setOrganizationGroups] = useState([]);
  const [orgSort, setOrgSort] = useState({ field: 'name', dir: 'asc' });
  const [orgGroupFilter, setOrgGroupFilter] = useState('');
  const [orgStateFilter, setOrgStateFilter] = useState('');
  const [orgCityFilter, setOrgCityFilter] = useState('');
  const [orgSizeMin, setOrgSizeMin] = useState('');
  const [orgSizeMax, setOrgSizeMax] = useState('');
  const [editingOrgGroupType, setEditingOrgGroupType] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [creatingOrg, setCreatingOrg] = useState(null);
  const [orgFiltersOpen, setOrgFiltersOpen] = useState(false);

  const [editingEmail, setEditingEmail] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState('');

  const [feedback, setFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState('');
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState('');
  const [feedbackReadFilter, setFeedbackReadFilter] = useState('');
  const [feedbackPriorityFilter, setFeedbackPriorityFilter] = useState('');
  const [feedbackUnreadCount, setFeedbackUnreadCount] = useState(0);
  const [feedbackSort, setFeedbackSort] = useState('newest');
  const [feedbackFiltersOpen, setFeedbackFiltersOpen] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [feedbackNotes, setFeedbackNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [closeoutNote, setCloseoutNote] = useState('');
  const [closeoutSubmitting, setCloseoutSubmitting] = useState(false);

  useEffect(() => {
    API.getAdminFeedbackUnreadCount()
      .then(res => setFeedbackUnreadCount(res.data.count))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== 'feedback') return;
    setFeedbackLoading(true);
    API.getAdminFeedback({})
      .then(res => {
        setFeedback(res.data);
        setFeedbackUnreadCount(res.data.filter(f => !f.is_read).length);
      })
      .catch(() => {})
      .finally(() => setFeedbackLoading(false));
  }, [activeTab]);

  useEffect(() => {
    if (!expandedFeedback) { setFeedbackNotes([]); return; }
    API.getFeedbackNotes(expandedFeedback)
      .then(res => setFeedbackNotes(res.data))
      .catch(() => {});
    const item = feedback.find(f => f.id === expandedFeedback);
    setCloseoutNote(item?.closeout_note || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedFeedback]);

  const handleAddNote = async () => {
    if (!newNote.trim() || !expandedFeedback) return;
    setNoteSubmitting(true);
    try {
      const res = await API.addFeedbackNote(expandedFeedback, newNote.trim());
      setFeedbackNotes(prev => [...prev, res.data]);
      setNewNote('');
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleFeedbackUpdate = async (id, data) => {
    try {
      await API.updateAdminFeedback(id, data);
      setFeedback(prev => {
        const next = prev.map(f => f.id === id ? { ...f, ...data } : f);
        const count = next.filter(f => !f.is_read).length;
        setFeedbackUnreadCount(count);
        window.dispatchEvent(new CustomEvent('feedbackUnreadCount', { detail: { count } }));
        return next;
      });
    } catch (err) {
      console.error('Failed to update feedback:', err);
    }
  };

  const handleFeedbackOpen = (id) => {
    setExpandedFeedback(id);
    const item = feedback.find(f => f.id === id);
    if (item && !item.is_read) handleFeedbackUpdate(id, { is_read: true });
  };

  const handleCloseout = async () => {
    if (!closeoutNote.trim() || !expandedFeedback) return;
    setCloseoutSubmitting(true);
    try {
      await API.closeoutFeedback(expandedFeedback, closeoutNote.trim());
      setFeedback(prev => prev.map(f => f.id === expandedFeedback
        ? { ...f, status: 'Resolved', closeout_note: closeoutNote.trim() }
        : f));
    } catch (err) {
      console.error('Failed to close out feedback:', err);
    } finally {
      setCloseoutSubmitting(false);
    }
  };

  const [appSettings, setAppSettings] = useState({ subscriptions_enabled: false, historical_legislation_enabled: false, enabled_sessions: null, ai_summary_sessions: null });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [availableSessions, setAvailableSessions] = useState([]);

  useEffect(() => {
    if (!isAdmin) navigate('/dashboard');
  }, [isAdmin, navigate]);

  useEffect(() => {
    API.adminGetSettings()
      .then((res) => setAppSettings(res.data))
      .catch(() => {})
      .finally(() => setSettingsLoading(false));
    API.adminGetAllSessions()
      .then((res) => setAvailableSessions(res.data))
      .catch(() => {});
  }, []);

  const saveSettings = async (next) => {
    setSettingsSaving(true);
    try {
      await API.adminUpdateSettings(next);
      setAppSettings(next);
    } catch (err) {
      console.error('Failed to save setting:', err);
    } finally {
      setSettingsSaving(false);
    }
  };

  const toggleSetting = (key) => saveSettings({ ...appSettings, [key]: !appSettings[key] });

  const toggleSession = (session) => {
    const allEnabled = appSettings.enabled_sessions === null;
    let next;
    if (allEnabled) {
      next = availableSessions.filter(s => s !== session);
    } else {
      const current = appSettings.enabled_sessions || [];
      if (current.includes(session)) {
        next = current.filter(s => s !== session);
      } else {
        next = [...current, session];
        if (next.length === availableSessions.length) next = null;
      }
    }
    saveSettings({ ...appSettings, enabled_sessions: next });
  };

  const toggleAllSessions = (enableAll) =>
    saveSettings({ ...appSettings, enabled_sessions: enableAll ? null : [] });

  const toggleAiSession = (session) => {
    const current = appSettings.ai_summary_sessions || [];
    const next = current.includes(session)
      ? current.filter(s => s !== session)
      : [...current, session];
    saveSettings({ ...appSettings, ai_summary_sessions: next.length === 0 ? null : next });
  };

  const toggleAllAiSessions = (enableAll) =>
    saveSettings({ ...appSettings, ai_summary_sessions: enableAll ? [...availableSessions] : null });

  const loadUsers = useCallback(async (search = '', letter = '') => {
    setUsersLoading(true);
    try {
      const res = await API.adminGetUsers(search, letter);
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadTeams = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const res = await API.adminGetTeams();
      setTeams(res.data);
    } catch (err) {
      console.error('Failed to load teams:', err);
    } finally {
      setTeamsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadTeams();
  }, [loadUsers, loadTeams]);

  const loadOrganizations = useCallback(async () => {
    setOrganizationsLoading(true);
    try {
      const res = await API.adminGetOrganizations();
      setOrganizations(res.data);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    } finally {
      setOrganizationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'organizations') return;
    loadOrganizations();
  }, [activeTab, loadOrganizations]);

  useEffect(() => {
    if (activeTab !== 'organizations' || organizationGroups.length > 0) return;
    API.getOrganizationGroups().then(res => setOrganizationGroups(res.data)).catch(() => {});
  }, [activeTab, organizationGroups.length]);

  useEffect(() => {
    if (!editingOrg) return;
    const modalEl = document.getElementById('editOrgProfileModal');
    if (!modalEl || !window.bootstrap) return;
    const instance = window.bootstrap.Modal.getOrCreateInstance(modalEl);
    instance.show();
    const handleHidden = () => setEditingOrg(null);
    modalEl.addEventListener('hidden.bs.modal', handleHidden);
    return () => modalEl.removeEventListener('hidden.bs.modal', handleHidden);
  }, [editingOrg]);

  const startEditOrgGroupType = (org) => setEditingOrgGroupType(org.organization_id);
  const cancelEditOrgGroupType = () => setEditingOrgGroupType(null);

  const saveOrgGroupType = async (org, newGroupType) => {
    if (newGroupType === org.group_type) { cancelEditOrgGroupType(); return; }
    const key = `orgtype_${org.organization_id}`;
    setSavingKey(key, true);
    try {
      await API.updateOrganizationInfo({ organization_id: org.organization_id, group_type: newGroupType });
      setOrganizations(prev => prev.map(o => o.organization_id === org.organization_id ? { ...o, group_type: newGroupType } : o));
    } catch (err) {
      console.error('Failed to update organization group type:', err);
    } finally {
      setSavingKey(key, false);
      cancelEditOrgGroupType();
    }
  };

  const toggleOrgDirectoryVisible = async (org) => {
    const key = `orgdirectory_${org.organization_id}`;
    setSavingKey(key, true);
    const next = !org.show_on_directory;
    try {
      await API.updateOrganizationInfo({ organization_id: org.organization_id, show_on_directory: next });
      setOrganizations(prev => prev.map(o => o.organization_id === org.organization_id ? { ...o, show_on_directory: next } : o));
    } catch (err) {
      console.error('Failed to update organization directory visibility:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const handleUserSearch = (e) => {
    e.preventDefault();
    setLetterFilter('');
    loadUsers(userSearch, '');
  };

  const handleLetterClick = (letter) => {
    const next = letterFilter === letter ? '' : letter;
    setLetterFilter(next);
    setUserSearch('');
    loadUsers('', next);
  };

  const setSavingKey = (key, val) => setSaving((s) => ({ ...s, [key]: val }));

  const addTrialDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const addTrialMonths = (months) => {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toISOString().slice(0, 10);
  };

  const closeTrialPrompt = () => {
    setTrialPrompt(null);
  };

  const grantUserSubscription = async (user, endDate) => {
    const key = `sub_${user.user_id}`;
    setSavingKey(key, true);
    try {
      await API.adminSetUserSubscription(user.user_id, true, endDate || null);
      setUsers((prev) =>
        prev.map((u) => u.user_id === user.user_id ? { ...u, individual_subscription: true, subscription_end: endDate || null } : u)
      );
    } catch (err) {
      console.error('Failed to update subscription:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const revokeUserSubscription = async (user) => {
    const key = `sub_${user.user_id}`;
    setSavingKey(key, true);
    try {
      await API.adminSetUserSubscription(user.user_id, false, null);
      setUsers((prev) =>
        prev.map((u) => u.user_id === user.user_id ? { ...u, individual_subscription: false, subscription_end: null } : u)
      );
    } catch (err) {
      console.error('Failed to update subscription:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const toggleUserSubscription = (user) => {
    if (user.individual_subscription) revokeUserSubscription(user);
    else setTrialPrompt({ kind: 'individual', entity: user });
  };

  const grantUserWorkplace = async (user, endDate) => {
    const key = `work_${user.user_id}`;
    setSavingKey(key, true);
    try {
      await API.adminSetUserWorkplace(user.user_id, true, endDate || null);
      setUsers((prev) =>
        prev.map((u) => u.user_id === user.user_id ? { ...u, workplace_subscription: true, workplace_subscription_end: endDate || null } : u)
      );
    } catch (err) {
      console.error('Failed to update workplace access:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const revokeUserWorkplace = async (user) => {
    const key = `work_${user.user_id}`;
    setSavingKey(key, true);
    try {
      await API.adminSetUserWorkplace(user.user_id, false, null);
      setUsers((prev) =>
        prev.map((u) => u.user_id === user.user_id ? { ...u, workplace_subscription: false, workplace_subscription_end: null } : u)
      );
    } catch (err) {
      console.error('Failed to update workplace access:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const toggleUserWorkplace = (user) => {
    if (user.workplace_subscription) revokeUserWorkplace(user);
    else setTrialPrompt({ kind: 'workplace', entity: user });
  };

  const toggleContributor = async (user) => {
    const key = `contrib_${user.user_id}`;
    setSavingKey(key, true);
    const next = !user.contributor;
    try {
      await API.adminSetContributor(user.user_id, next);
      setUsers((prev) =>
        prev.map((u) => u.user_id === user.user_id ? { ...u, contributor: next } : u)
      );
      if (user.user_id === auth.user?.user_id) refreshStatus();
    } catch (err) {
      console.error('Failed to update contributor:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const toggleAmbassador = async (user) => {
    const key = `ambassador_${user.user_id}`;
    setSavingKey(key, true);
    const next = !user.is_ambassador;
    try {
      if (next) await API.adminSetUserAmbassador(user.user_id);
      else await API.adminUnsetUserAmbassador(user.user_id);
      setUsers((prev) =>
        prev.map((u) => u.user_id === user.user_id ? { ...u, is_ambassador: next } : u)
      );
      if (user.user_id === auth.user?.user_id) refreshStatus();
    } catch (err) {
      console.error('Failed to update ambassador status:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const startEditEmail = (user) => {
    setEditingEmail(user.user_id);
    setEmailDraft(user.email);
    setEmailError('');
  };

  const cancelEditEmail = () => {
    setEditingEmail(null);
    setEmailDraft('');
    setEmailError('');
  };

  const saveEmail = async (user_id) => {
    const trimmed = emailDraft.trim();
    if (!trimmed) return;
    if (trimmed === users.find(u => u.user_id === user_id)?.email) {
      cancelEditEmail();
      return;
    }
    setSavingKey(`email_${user_id}`, true);
    setEmailError('');
    try {
      const res = await API.adminUpdateUserEmail(user_id, trimmed);
      setUsers(prev => prev.map(u => u.user_id === user_id ? { ...u, email: res.data.email } : u));
      cancelEditEmail();
    } catch (err) {
      setEmailError(err.response?.data?.msg || 'Failed to update email.');
    } finally {
      setSavingKey(`email_${user_id}`, false);
    }
  };

  const grantTeamSubscription = async (team, endDate) => {
    const key = `team_${team.team_id}`;
    setSavingKey(key, true);
    try {
      await API.adminSetTeamSubscription(team.team_id, true, endDate || null);
      setTeams((prev) =>
        prev.map((t) => t.team_id === team.team_id ? { ...t, workplace_subscription: true, sub_end: endDate || null } : t)
      );
    } catch (err) {
      console.error('Failed to update team subscription:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const revokeTeamSubscription = async (team) => {
    const key = `team_${team.team_id}`;
    setSavingKey(key, true);
    try {
      await API.adminSetTeamSubscription(team.team_id, false, null);
      setTeams((prev) =>
        prev.map((t) => t.team_id === team.team_id ? { ...t, workplace_subscription: false, sub_end: null } : t)
      );
    } catch (err) {
      console.error('Failed to update team subscription:', err);
    } finally {
      setSavingKey(key, false);
    }
  };

  const toggleTeamSubscription = (team) => {
    if (team.workplace_subscription) revokeTeamSubscription(team);
    else setTrialPrompt({ kind: 'team', entity: team });
  };

  const confirmTrial = (endDate) => {
    if (!trialPrompt) return;
    const { kind, entity } = trialPrompt;
    closeTrialPrompt();
    if (kind === 'individual') grantUserSubscription(entity, endDate);
    else if (kind === 'workplace') grantUserWorkplace(entity, endDate);
    else if (kind === 'team') grantTeamSubscription(entity, endDate);
  };

  const openUserDetail = async (user_id) => {
    setUserDetail(null);
    setUserActivity(null);
    setLoadingDetail(true);
    try {
      const [detailRes, activityRes] = await Promise.all([
        API.adminGetUserDetail(user_id),
        API.adminGetUserActivity(user_id),
      ]);
      setUserDetail(detailRes.data);
      setUserActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to load user detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const openTeamDetail = async (team_id) => {
    setTeamDetail(null);
    setLoadingTeamDetail(true);
    setOrgSearch('');
    setOrgResults([]);
    setMemberSearch('');
    setMemberResults([]);
    try {
      const res = await API.adminGetTeamDetail(team_id);
      setTeamDetail(res.data);
      setAmbassadorMonthly(res.data.ambassador_commission_rate_monthly != null ? String(res.data.ambassador_commission_rate_monthly) : '');
      setAmbassadorAnnual(res.data.ambassador_commission_rate_annual != null ? String(res.data.ambassador_commission_rate_annual) : '');
    } catch (err) {
      console.error('Failed to load team detail:', err);
    } finally {
      setLoadingTeamDetail(false);
    }
  };

  const closeTeamDetail = () => {
    setTeamDetail(null);
    setLoadingTeamDetail(false);
    setOrgSearch('');
    setOrgResults([]);
    setMemberSearch('');
    setMemberResults([]);
    setAmbassadorMonthly('');
    setAmbassadorAnnual('');
  };

  const toggleTeamAmbassador = async () => {
    if (!teamDetail) return;
    setSavingAmbassador(true);
    try {
      if (teamDetail.ambassador_enabled) {
        await API.adminDisableTeamAmbassador(teamDetail.team_id);
        setTeamDetail(prev => ({ ...prev, ambassador_enabled: false }));
      } else {
        const res = await API.adminEnableTeamAmbassador(teamDetail.team_id, {
          commission_rate_monthly: ambassadorMonthly,
          commission_rate_annual: ambassadorAnnual,
        });
        setTeamDetail(prev => ({ ...prev, ...res.data }));
        setAmbassadorMonthly(String(res.data.ambassador_commission_rate_monthly));
        setAmbassadorAnnual(String(res.data.ambassador_commission_rate_annual));
      }
    } catch (err) {
      console.error('Failed to toggle workspace ambassador program:', err);
    } finally {
      setSavingAmbassador(false);
    }
  };

  const saveTeamAmbassadorRate = async () => {
    if (!teamDetail) return;
    setSavingAmbassador(true);
    try {
      const res = await API.adminUpdateTeamAmbassadorRate(teamDetail.team_id, {
        commission_rate_monthly: ambassadorMonthly,
        commission_rate_annual: ambassadorAnnual,
      });
      setTeamDetail(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      console.error('Failed to update workspace ambassador rate:', err);
    } finally {
      setSavingAmbassador(false);
    }
  };

  const handleOrgSearch = (q) => {
    setOrgSearch(q);
    setOrgResults([]);
    if (!q.trim()) return;
    clearTimeout(orgSearchTimer.current);
    orgSearchTimer.current = setTimeout(async () => {
      setOrgSearching(true);
      try {
        const res = await API.searchOrganizations(q);
        setOrgResults(res.data || []);
      } catch (err) {
        console.error('Org search error:', err);
      } finally {
        setOrgSearching(false);
      }
    }, 350);
  };

  const updateTeamOrg = async (org) => {
    if (!teamDetail) return;
    setSavingOrg(true);
    try {
      await API.adminSetTeamOrganization(teamDetail.team_id, org ? org.organization_id : null);
      setTeamDetail(prev => ({ ...prev, organization_id: org?.organization_id ?? null, Organization: org ?? null }));
      setOrgSearch('');
      setOrgResults([]);
    } catch (err) {
      console.error('Failed to update org:', err);
    } finally {
      setSavingOrg(false);
    }
  };

  const handleMemberSearch = (q) => {
    setMemberSearch(q);
    setMemberResults([]);
    if (!q.trim()) return;
    clearTimeout(memberSearchTimer.current);
    memberSearchTimer.current = setTimeout(async () => {
      setMemberSearching(true);
      try {
        const res = await API.adminGetUsers(q, '');
        setMemberResults(res.data || []);
      } catch (err) {
        console.error('Member search error:', err);
      } finally {
        setMemberSearching(false);
      }
    }, 350);
  };

  const addTeamMember = async (user) => {
    if (!teamDetail) return;
    setAddingMember(user.user_id);
    try {
      await API.adminAddTeamMember(teamDetail.team_id, user.user_id);
      const res = await API.adminGetTeamDetail(teamDetail.team_id);
      setTeamDetail(res.data);
      setMemberSearch('');
      setMemberResults([]);
    } catch (err) {
      if (err.response?.status === 409) {
        alert('User is already a member of this workspace.');
      } else {
        console.error('Failed to add member:', err);
      }
    } finally {
      setAddingMember('');
    }
  };

  const removeTeamMember = async (member_id) => {
    if (!teamDetail) return;
    setRemovingMember(member_id);
    try {
      await API.adminRemoveTeamMember(teamDetail.team_id, member_id);
      setTeamDetail(prev => ({ ...prev, TeamMembers: prev.TeamMembers.filter(m => m.member_id !== member_id) }));
    } catch (err) {
      console.error('Failed to remove member:', err);
    } finally {
      setRemovingMember('');
    }
  };

  const filteredOrganizations = organizations.filter(org => {
    if (orgGroupFilter && org.group_type !== orgGroupFilter) return false;
    if (orgStateFilter && org.state !== orgStateFilter) return false;
    if (orgCityFilter && !(org.city || '').toLowerCase().includes(orgCityFilter.toLowerCase())) return false;
    const employees = Number(org.employee_count) || 0;
    if (orgSizeMin !== '' && employees < Number(orgSizeMin)) return false;
    if (orgSizeMax !== '' && employees > Number(orgSizeMax)) return false;
    return true;
  });

  const sortedOrganizations = [...filteredOrganizations].sort((a, b) => {
    const dir = orgSort.dir === 'asc' ? 1 : -1;
    if (orgSort.field === 'employees') {
      return dir * ((Number(a.employee_count) || 0) - (Number(b.employee_count) || 0));
    }
    if (orgSort.field === 'group_type') {
      return dir * (a.group_type || '').localeCompare(b.group_type || '');
    }
    if (orgSort.field === 'location') {
      const aLoc = `${a.state || ''} ${a.city || ''}`.toLowerCase();
      const bLoc = `${b.state || ''} ${b.city || ''}`.toLowerCase();
      return dir * aLoc.localeCompare(bLoc);
    }
    if (orgSort.field === 'workspace') {
      const aWorkspace = a.TeamAccounts?.[0]?.team_name || '';
      const bWorkspace = b.TeamAccounts?.[0]?.team_name || '';
      return dir * aWorkspace.localeCompare(bWorkspace);
    }
    if (orgSort.field === 'directory') {
      return dir * ((a.show_on_directory ? 1 : 0) - (b.show_on_directory ? 1 : 0));
    }
    return dir * (a.organization_name || '').localeCompare(b.organization_name || '');
  });

  const uniqueOrgStates = [...new Set(organizations.map(o => o.state).filter(Boolean))].sort();

  // Merge the canonical group-type list with whatever group_type values actually
  // appear on organizations (e.g. legacy/free-text values like "Other") so every
  // organization is filterable/selectable, not just ones matching the canonical list.
  const allOrgGroupTypes = [...new Set([
    ...organizationGroups.map(g => g.group_name),
    ...organizations.map(o => o.group_type).filter(Boolean),
  ])].sort((a, b) => a.localeCompare(b));

  const orgActiveFilterCount = [orgGroupFilter, orgStateFilter, orgCityFilter].filter(Boolean).length
    + (orgSizeMin !== '' ? 1 : 0) + (orgSizeMax !== '' ? 1 : 0);
  const orgFiltersActive = orgActiveFilterCount > 0;

  const clearOrgFilters = () => {
    setOrgGroupFilter('');
    setOrgStateFilter('');
    setOrgCityFilter('');
    setOrgSizeMin('');
    setOrgSizeMax('');
  };

  const renderOrgSortArrow = (field) => orgSort.field === field ? (orgSort.dir === 'asc' ? '↑' : '↓') : '';
  const toggleOrgSort = (field) => setOrgSort(s => ({ field, dir: s.field === field && s.dir === 'asc' ? 'desc' : 'asc' }));

  if (!isAdmin) return null;

  return (
    <>
    <div className="admin-page">
      <PageTitle title="Admin Panel | 'elloGovna" />
      <div className="admin-header">
        <h1 className="admin-title">
          <i className="bi bi-shield-lock me-2" />
          Admin Panel
        </h1>
        <p className="admin-subtitle">Manage user and workspace access</p>
      </div>

      {/* Tab nav */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <i className="bi bi-people me-1" /> Users
        </button>
        <button
          className={`admin-tab ${activeTab === 'teams' ? 'active' : ''}`}
          onClick={() => setActiveTab('teams')}
        >
          <i className="bi bi-briefcase me-1" /> Workspaces
        </button>
        <button
          className={`admin-tab ${activeTab === 'organizations' ? 'active' : ''}`}
          onClick={() => setActiveTab('organizations')}
        >
          <i className="bi bi-building me-1" /> Organizations
        </button>
        <button
          className={`admin-tab ${activeTab === 'awards' ? 'active' : ''}`}
          onClick={() => setActiveTab('awards')}
        >
          <i className="bi bi-award me-1" /> Awards
        </button>
        <button
          className={`admin-tab ${activeTab === 'profiles' ? 'active' : ''}`}
          onClick={() => setActiveTab('profiles')}
        >
          <i className="bi bi-person-plus me-1" /> Create Profile
        </button>
        <button
          className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <i className="bi bi-toggles me-1" /> Settings
        </button>
        <button
          className={`admin-tab ${activeTab === 'issues' ? 'active' : ''}`}
          onClick={() => setActiveTab('issues')}
        >
          <i className="bi bi-tags me-1" /> Issue Areas
        </button>
        <button
          className={`admin-tab ${activeTab === 'hearings' ? 'active' : ''}`}
          onClick={() => setActiveTab('hearings')}
        >
          <i className="bi bi-calendar-event me-1" /> Hearings
        </button>
        <button
          className={`admin-tab ${activeTab === 'legislative-transition' ? 'active' : ''}`}
          onClick={() => setActiveTab('legislative-transition')}
        >
          <i className="bi bi-arrow-left-right me-1" /> Legislative Transition
        </button>
        <button
          className={`admin-tab ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          <i className="bi bi-chat-square-text me-1" /> Feedback
          {feedbackUnreadCount > 0 && (
            <span className="af-tab-count">{feedbackUnreadCount >= 100 ? '99+' : feedbackUnreadCount}</span>
          )}
        </button>
      </div>

      {/* ── Users tab ── */}
      {activeTab === 'users' && (
        <div className="admin-section">
          <div className="awards-subtabs mb-3">
            <button
              className={`awards-subtab ${usersSubTab === 'registered' ? 'active' : ''}`}
              onClick={() => setUsersSubTab('registered')}
            >
              <i className="bi bi-people me-1" />Registered Users
            </button>
            <button
              className={`awards-subtab ${usersSubTab === 'approved-emails' ? 'active' : ''}`}
              onClick={() => setUsersSubTab('approved-emails')}
            >
              <i className="bi bi-envelope-check me-1" />Approved Emails
            </button>
            <button
              className={`awards-subtab ${usersSubTab === 'approved-domains' ? 'active' : ''}`}
              onClick={() => setUsersSubTab('approved-domains')}
            >
              <i className="bi bi-globe me-1" />Approved Domains
            </button>
            <button
              className={`awards-subtab ${usersSubTab === 'ambassadors' ? 'active' : ''}`}
              onClick={() => setUsersSubTab('ambassadors')}
            >
              <i className="bi bi-megaphone me-1" />Ambassadors
            </button>
          </div>

          {usersSubTab === 'approved-emails' && <AdminApprovedEmails />}
          {usersSubTab === 'approved-domains' && <AdminApprovedDomains />}
          {usersSubTab === 'ambassadors' && <AdminAmbassadors onActiveChange={loadUsers} />}

          {usersSubTab === 'registered' && (
          <>
          <form className="admin-search-form" onSubmit={handleUserSearch}>
            <input
              className="admin-search-input"
              placeholder="Search by name or email…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <button className="admin-search-btn" type="submit">
              <i className="bi bi-search" />
            </button>
            {(userSearch || letterFilter) && (
              <button
                className="admin-clear-btn"
                type="button"
                onClick={() => { setUserSearch(''); setLetterFilter(''); loadUsers('', ''); }}
              >
                <i className="bi bi-x" />
              </button>
            )}
          </form>

          <div className="admin-alpha-bar">
            {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(l => (
              <button
                key={l}
                type="button"
                className={`admin-alpha-btn${letterFilter === l ? ' admin-alpha-btn--active' : ''}`}
                onClick={() => handleLetterClick(l)}
              >
                {l}
              </button>
            ))}
          </div>

          {usersLoading ? (
            <div className="admin-loading">Loading…</div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="admin-th-sortable" onClick={() => setUserSort(s => ({ field: 'name', dir: s.field === 'name' && s.dir === 'asc' ? 'desc' : 'asc' }))}>
                      Name {userSort.field === 'name' ? (userSort.dir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="admin-th-sortable admin-th-center" onClick={() => setUserSort(s => ({ field: 'joined', dir: s.field === 'joined' && s.dir === 'asc' ? 'desc' : 'asc' }))}>
                      Joined {userSort.field === 'joined' ? (userSort.dir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="admin-th-sortable admin-th-center" onClick={() => setUserSort(s => ({ field: 'last_active', dir: s.field === 'last_active' && s.dir === 'asc' ? 'desc' : 'asc' }))}>
                      Last Active {userSort.field === 'last_active' ? (userSort.dir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th>Email</th>
                    <th className="admin-th-center">Individual Subscription</th>
                    <th className="admin-th-center">Workspace Subscription</th>
                    <th className="admin-th-center">Concierge</th>
                    <th className="admin-th-center">Ambassador</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr><td colSpan={8} className="admin-empty">No users found</td></tr>
                  ) : [...users].sort((a, b) => {
                      const dir = userSort.dir === 'asc' ? 1 : -1;
                      if (userSort.field === 'joined') {
                        return dir * ((new Date(a.created_at) || 0) - (new Date(b.created_at) || 0));
                      }
                      if (userSort.field === 'last_active') {
                        if (!a.last_active && !b.last_active) return 0;
                        if (!a.last_active) return 1;
                        if (!b.last_active) return -1;
                        return dir * (new Date(a.last_active) - new Date(b.last_active));
                      }
                      const aName = `${a.last_name} ${a.first_name}`.toLowerCase();
                      const bName = `${b.last_name} ${b.first_name}`.toLowerCase();
                      return dir * aName.localeCompare(bName);
                    }).map((user) => (
                    <tr key={user.user_id}>
                      <td className="admin-name" data-label="Name">
                        <button className="admin-name-btn" onClick={() => openUserDetail(user.user_id)}>
                          {user.first_name} {user.last_name}
                        </button>
                      </td>
                      <td className="admin-td-center admin-joined" data-label="Joined">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="admin-td-center admin-joined" data-label="Last Active">
                        {user.last_active ? new Date(user.last_active).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="admin-email" data-label="Email">
                        {editingEmail === user.user_id ? (
                          <div className="admin-email-edit">
                            <input
                              className="admin-email-input"
                              type="email"
                              value={emailDraft}
                              onChange={e => { setEmailDraft(e.target.value); setEmailError(''); }}
                              onKeyDown={e => { if (e.key === 'Enter') saveEmail(user.user_id); if (e.key === 'Escape') cancelEditEmail(); }}
                              autoFocus
                            />
                            <div className="admin-email-actions">
                              <button
                                className="admin-email-save"
                                onClick={() => saveEmail(user.user_id)}
                                disabled={saving[`email_${user.user_id}`]}
                                title="Save"
                              >
                                {saving[`email_${user.user_id}`] ? <i className="bi bi-arrow-repeat admin-spin" /> : <i className="bi bi-check-lg" />}
                              </button>
                              <button className="admin-email-cancel" onClick={cancelEditEmail} title="Cancel">
                                <i className="bi bi-x-lg" />
                              </button>
                            </div>
                            {emailError && <div className="admin-email-error">{emailError}</div>}
                          </div>
                        ) : (
                          <span className="admin-email-text" onClick={() => startEditEmail(user)} title="Click to edit">
                            {user.email}
                          </span>
                        )}
                      </td>
                      <td className="admin-td-center" data-label="Indiv. Sub">
                        <button
                          className={`admin-toggle ${user.individual_subscription ? 'on' : 'off'}`}
                          onClick={() => toggleUserSubscription(user)}
                          disabled={saving[`sub_${user.user_id}`]}
                          title={user.individual_subscription ? 'Revoke access' : 'Grant access'}
                        >
                          {saving[`sub_${user.user_id}`]
                            ? <i className="bi bi-arrow-repeat admin-spin" />
                            : user.individual_subscription
                            ? <i className="bi bi-toggle-on" />
                            : <i className="bi bi-toggle-off" />}
                        </button>
                        {user.individual_subscription && user.subscription_end && (
                          <div className="admin-trial-badge">
                            Ends {new Date(user.subscription_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </td>
                      <td className="admin-td-center" data-label="WS Sub">
                        <button
                          className={`admin-toggle ${user.workplace_subscription ? 'on' : 'off'}`}
                          onClick={() => toggleUserWorkplace(user)}
                          disabled={saving[`work_${user.user_id}`]}
                          title={user.workplace_subscription ? 'Revoke workspace access' : 'Grant workspace access'}
                        >
                          {saving[`work_${user.user_id}`]
                            ? <i className="bi bi-arrow-repeat admin-spin" />
                            : user.workplace_subscription
                            ? <i className="bi bi-toggle-on" />
                            : <i className="bi bi-toggle-off" />}
                        </button>
                        {user.workplace_subscription && user.workplace_subscription_end && (
                          <div className="admin-trial-badge">
                            Ends {new Date(user.workplace_subscription_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </td>
                      <td className="admin-td-center" data-label="Concierge">
                        <button
                          className={`admin-toggle ${user.contributor ? 'on' : 'off'}`}
                          onClick={() => toggleContributor(user)}
                          disabled={saving[`contrib_${user.user_id}`]}
                          title={user.contributor ? 'Remove contributor' : 'Make contributor'}
                        >
                          {saving[`contrib_${user.user_id}`]
                            ? <i className="bi bi-arrow-repeat admin-spin" />
                            : user.contributor
                            ? <i className="bi bi-toggle-on" />
                            : <i className="bi bi-toggle-off" />}
                        </button>
                      </td>
                      <td className="admin-td-center" data-label="Ambassador">
                        <button
                          className={`admin-toggle ${user.is_ambassador ? 'on' : 'off'}`}
                          onClick={() => toggleAmbassador(user)}
                          disabled={saving[`ambassador_${user.user_id}`]}
                          title={user.is_ambassador ? 'Remove ambassador' : 'Make ambassador'}
                        >
                          {saving[`ambassador_${user.user_id}`]
                            ? <i className="bi bi-arrow-repeat admin-spin" />
                            : user.is_ambassador
                            ? <i className="bi bi-toggle-on" />
                            : <i className="bi bi-toggle-off" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
          )}
        </div>
      )}

      {/* ── Create Profile tab ── */}
      {activeTab === 'profiles' && (
        <div className="admin-section">
          <AdminCreateProfile />
        </div>
      )}

      {/* ── Awards tab ── */}
      {activeTab === 'awards' && (
        <div className="admin-section">
          <AdminAwards />
        </div>
      )}

      {/* ── Workspaces tab ── */}
      {activeTab === 'teams' && (
        <div className="admin-section">
          {teamsLoading ? (
            <div className="admin-loading">Loading…</div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Workspace Name</th>
                    <th className="admin-th-center">Workspace Access</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.length === 0 ? (
                    <tr><td colSpan={2} className="admin-empty">No workspaces found</td></tr>
                  ) : teams.map((team) => (
                    <tr key={team.team_id}>
                      <td className="admin-name" data-label="Workspace">
                        <button className="admin-name-btn" onClick={() => openTeamDetail(team.team_id)}>
                          {team.team_name}
                        </button>
                      </td>
                      <td className="admin-td-center" data-label="Access">
                        <button
                          className={`admin-toggle ${team.workplace_subscription ? 'on' : 'off'}`}
                          onClick={() => toggleTeamSubscription(team)}
                          disabled={saving[`team_${team.team_id}`]}
                          title={team.workplace_subscription ? 'Revoke access' : 'Grant access'}
                        >
                          {saving[`team_${team.team_id}`]
                            ? <i className="bi bi-arrow-repeat admin-spin" />
                            : team.workplace_subscription
                            ? <i className="bi bi-toggle-on" />
                            : <i className="bi bi-toggle-off" />}
                        </button>
                        {team.workplace_subscription && team.sub_end && (
                          <div className="admin-trial-badge">
                            Ends {new Date(team.sub_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Organizations tab ── */}
      {activeTab === 'organizations' && (
        <div className="admin-section">
          <div className="af-filter-bar">
            <div className="af-filter-bar-row">
              <button
                className={`af-filter-toggle${orgFiltersOpen ? ' af-filter-toggle--open' : ''}`}
                onClick={() => setOrgFiltersOpen(o => !o)}
              >
                <i className="bi bi-funnel" />
                Filters
                {orgActiveFilterCount > 0 && <span className="af-filter-count">{orgActiveFilterCount}</span>}
              </button>
              {orgFiltersActive && (
                <button className="af-filter-clear" onClick={clearOrgFilters}>
                  Clear all
                </button>
              )}
              <button className="admin-create-org-btn" onClick={() => setCreatingOrg({ mode: 'organization' })}>
                <i className="bi bi-plus-lg" /> Create
              </button>
            </div>
            <div className={`af-filter-panel${orgFiltersOpen ? ' af-filter-panel--open' : ''}`}>
              <div className="acp-field">
                <label className="acp-label">Group Type</label>
                <select
                  className="acp-select"
                  value={orgGroupFilter}
                  onChange={(e) => setOrgGroupFilter(e.target.value)}
                >
                  <option value="">All Group Types</option>
                  {allOrgGroupTypes.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="acp-field">
                <label className="acp-label">State</label>
                <select
                  className="acp-select"
                  value={orgStateFilter}
                  onChange={(e) => setOrgStateFilter(e.target.value)}
                >
                  <option value="">All States</option>
                  {uniqueOrgStates.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="acp-field">
                <label className="acp-label">City</label>
                <input
                  className="acp-input"
                  placeholder="City…"
                  value={orgCityFilter}
                  onChange={(e) => setOrgCityFilter(e.target.value)}
                />
              </div>
              <div className="acp-field">
                <label className="acp-label">Min Employees</label>
                <input
                  className="acp-input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={orgSizeMin}
                  onChange={(e) => setOrgSizeMin(e.target.value)}
                />
              </div>
              <div className="acp-field">
                <label className="acp-label">Max Employees</label>
                <input
                  className="acp-input"
                  type="number"
                  min="0"
                  placeholder="Any"
                  value={orgSizeMax}
                  onChange={(e) => setOrgSizeMax(e.target.value)}
                />
              </div>
              {orgFiltersActive && (
                <button className="af-filter-clear af-filter-clear--panel" onClick={clearOrgFilters}>
                  Clear all
                </button>
              )}
              <button className="admin-create-org-btn admin-create-org-btn--panel" onClick={() => setCreatingOrg({ mode: 'organization' })}>
                <i className="bi bi-plus-lg" /> Create
              </button>
            </div>
          </div>

          {organizationsLoading ? (
            <div className="admin-loading">Loading…</div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="admin-th-sortable" onClick={() => toggleOrgSort('name')}>
                      Organization {renderOrgSortArrow('name')}
                    </th>
                    <th className="admin-th-sortable" onClick={() => toggleOrgSort('group_type')}>
                      Group Type {renderOrgSortArrow('group_type')}
                    </th>
                    <th className="admin-th-sortable" onClick={() => toggleOrgSort('location')}>
                      Location {renderOrgSortArrow('location')}
                    </th>
                    <th className="admin-th-sortable" onClick={() => toggleOrgSort('workspace')}>
                      Linked Workspace {renderOrgSortArrow('workspace')}
                    </th>
                    <th className="admin-th-sortable admin-th-center" onClick={() => toggleOrgSort('employees')}>
                      Employees {renderOrgSortArrow('employees')}
                    </th>
                    <th className="admin-th-sortable admin-th-center" onClick={() => toggleOrgSort('directory')}>
                      Directory {renderOrgSortArrow('directory')}
                    </th>
                    <th className="admin-th-center">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOrganizations.length === 0 ? (
                    <tr><td colSpan={7} className="admin-empty">No organizations found</td></tr>
                  ) : sortedOrganizations.map((org) => (
                    <tr key={org.organization_id}>
                      <td className="admin-name" data-label="Organization">
                        <button className="admin-name-btn" onClick={() => navigate(`/organization/${org.organization_id}`)}>
                          {org.organization_name}
                        </button>
                      </td>
                      <td data-label="Group Type">
                        {editingOrgGroupType === org.organization_id ? (
                          <select
                            className="admin-org-group-select"
                            value={org.group_type || ''}
                            autoFocus
                            disabled={saving[`orgtype_${org.organization_id}`]}
                            onChange={(e) => saveOrgGroupType(org, e.target.value)}
                            onBlur={cancelEditOrgGroupType}
                          >
                            {allOrgGroupTypes.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="admin-org-group-text" onClick={() => startEditOrgGroupType(org)} title="Click to edit">
                            {saving[`orgtype_${org.organization_id}`]
                              ? <i className="bi bi-arrow-repeat admin-spin" />
                              : (org.group_type || '—')}
                          </span>
                        )}
                      </td>
                      <td data-label="Location">
                        {org.city || org.state
                          ? `${org.city || ''}${org.city && org.state ? ', ' : ''}${org.state || ''}`
                          : '—'}
                      </td>
                      <td data-label="Workspace">
                        {org.TeamAccounts && org.TeamAccounts.length > 0
                          ? org.TeamAccounts.map((t) => t.team_name).join(', ')
                          : '—'}
                      </td>
                      <td className="admin-td-center" data-label="Employees">
                        {Number(org.employee_count) || 0}
                      </td>
                      <td className="admin-td-center" data-label="Directory">
                        <button
                          className={`admin-toggle ${org.show_on_directory ? 'on' : 'off'}`}
                          onClick={() => toggleOrgDirectoryVisible(org)}
                          disabled={saving[`orgdirectory_${org.organization_id}`]}
                          title={org.show_on_directory ? 'Hide from Directory' : 'Show on Directory'}
                        >
                          {saving[`orgdirectory_${org.organization_id}`]
                            ? <i className="bi bi-arrow-repeat admin-spin" />
                            : org.show_on_directory
                            ? <i className="bi bi-toggle-on" />
                            : <i className="bi bi-toggle-off" />}
                        </button>
                      </td>
                      <td className="admin-td-center" data-label="Edit">
                        <button className="admin-org-edit-btn" onClick={() => setEditingOrg(org)} title="Edit organization">
                          <i className="bi bi-pencil-square" />
                        </button>
                        <button className="admin-org-edit-btn" onClick={() => setCreatingOrg({ mode: 'division', presetOrg: org })} title="Add division">
                          <i className="bi bi-diagram-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Settings tab ── */}
      {activeTab === 'settings' && (
        <div className="admin-section">
          {settingsLoading ? (
            <div className="admin-loading">Loading…</div>
          ) : (
            <div className="admin-settings-list">

              <div className="admin-setting-row">
                <div className="admin-setting-info">
                  <div className="admin-setting-label">Subscriptions</div>
                  <div className="admin-setting-desc">
                    When off, all logged-in users can access Legislation and Workspaces without a paid subscription (teaser mode). Turn on once Lemon Squeezy is approved.
                  </div>
                </div>
                <button
                  className={`admin-toggle ${appSettings.subscriptions_enabled ? 'on' : 'off'}`}
                  onClick={() => toggleSetting('subscriptions_enabled')}
                  disabled={settingsSaving}
                  title={appSettings.subscriptions_enabled ? 'Disable subscription gating' : 'Enable subscription gating'}
                >
                  {settingsSaving
                    ? <i className="bi bi-arrow-repeat admin-spin" />
                    : appSettings.subscriptions_enabled
                    ? <i className="bi bi-toggle-on" />
                    : <i className="bi bi-toggle-off" />}
                </button>
              </div>

              <div className="admin-setting-row">
                <div className="admin-setting-info">
                  <div className="admin-setting-label">Historical Legislation</div>
                  <div className="admin-setting-desc">
                    When off, the Legislation page only shows the current legislative session. Turn on to expose all imported historical sessions (1989–present).
                  </div>
                </div>
                <button
                  className={`admin-toggle ${appSettings.historical_legislation_enabled ? 'on' : 'off'}`}
                  onClick={() => toggleSetting('historical_legislation_enabled')}
                  disabled={settingsSaving}
                  title={appSettings.historical_legislation_enabled ? 'Hide historical sessions' : 'Show historical sessions'}
                >
                  {settingsSaving
                    ? <i className="bi bi-arrow-repeat admin-spin" />
                    : appSettings.historical_legislation_enabled
                    ? <i className="bi bi-toggle-on" />
                    : <i className="bi bi-toggle-off" />}
                </button>
              </div>

              {availableSessions.length > 0 && (
                <div className="admin-session-selector">
                  <div className="admin-session-selector-header">
                    <span className="admin-session-selector-label">AI Summary Sessions</span>
                    <div className="admin-session-selector-actions">
                      <button
                        className="admin-session-action-btn"
                        onClick={() => toggleAllAiSessions(true)}
                        disabled={settingsSaving || (appSettings.ai_summary_sessions !== null && appSettings.ai_summary_sessions?.length === availableSessions.length)}
                      >All</button>
                      <button
                        className="admin-session-action-btn"
                        onClick={() => toggleAllAiSessions(false)}
                        disabled={settingsSaving || !appSettings.ai_summary_sessions?.length}
                      >None</button>
                    </div>
                  </div>
                  <div className="admin-session-list">
                    {availableSessions.map(session => {
                      const enabled = (appSettings.ai_summary_sessions || []).includes(session);
                      return (
                        <label key={session} className={`admin-session-item${enabled ? ' enabled' : ''}`}>
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => toggleAiSession(session)}
                            disabled={settingsSaving}
                          />
                          {session}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {appSettings.historical_legislation_enabled && availableSessions.length > 0 && (
                <div className="admin-session-selector">
                  <div className="admin-session-selector-header">
                    <span className="admin-session-selector-label">Enabled Sessions</span>
                    <div className="admin-session-selector-actions">
                      <button
                        className="admin-session-action-btn"
                        onClick={() => toggleAllSessions(true)}
                        disabled={settingsSaving || appSettings.enabled_sessions === null}
                      >All</button>
                      <button
                        className="admin-session-action-btn"
                        onClick={() => toggleAllSessions(false)}
                        disabled={settingsSaving || (appSettings.enabled_sessions !== null && appSettings.enabled_sessions.length === 0)}
                      >None</button>
                    </div>
                  </div>
                  <div className="admin-session-list">
                    {availableSessions.map(session => {
                      const enabled = appSettings.enabled_sessions === null || (appSettings.enabled_sessions || []).includes(session);
                      return (
                        <label key={session} className={`admin-session-item${enabled ? ' enabled' : ''}`}>
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => toggleSession(session)}
                            disabled={settingsSaving}
                          />
                          {session}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      )}

      {/* ── Issue Areas tab ── */}
      {activeTab === 'issues' && (
        <div className="admin-section">
          <AdminIssueAreas />
        </div>
      )}

      {/* ── Hearings tab ── */}
      {activeTab === 'hearings' && (
        <div className="admin-section">
          <AdminHearings />
        </div>
      )}

      {/* ── Legislative Transition tab ── */}
      {activeTab === 'legislative-transition' && (
        <div className="admin-section">
          <AdminLegislativeTransition />
        </div>
      )}

      {/* ── Feedback tab ── */}
      {activeTab === 'feedback' && (
        <div className="admin-section">
          {feedbackLoading ? (
            <div className="text-center py-4"><div className="spinner-border spinner-border-sm" role="status" /></div>
          ) : (
            <>
            {(() => {
              const activeCount = [feedbackTypeFilter, feedbackFilter, feedbackReadFilter, feedbackPriorityFilter].filter(Boolean).length;
              return (
                <div className="af-filter-bar">
                  <div className="af-filter-bar-row">
                    <button
                      className={`af-filter-toggle${feedbackFiltersOpen ? ' af-filter-toggle--open' : ''}`}
                      onClick={() => setFeedbackFiltersOpen(o => !o)}
                    >
                      <i className="bi bi-funnel" />
                      Filters
                      {activeCount > 0 && <span className="af-filter-count">{activeCount}</span>}
                    </button>
                    {activeCount > 0 && (
                      <button
                        className="af-filter-clear"
                        onClick={() => { setFeedbackTypeFilter(''); setFeedbackFilter(''); setFeedbackReadFilter(''); setFeedbackPriorityFilter(''); setFeedbackSort('newest'); setExpandedFeedback(null); }}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className={`af-filter-panel${feedbackFiltersOpen ? ' af-filter-panel--open' : ''}`}>
                      <div className="acp-field">
                        <label className="acp-label">Category</label>
                        <select
                          className="acp-select"
                          value={feedbackTypeFilter}
                          onChange={e => { setFeedbackTypeFilter(e.target.value); setExpandedFeedback(null); }}
                        >
                          <option value="">All Categories</option>
                          <option value="Problem">Problem</option>
                          <option value="Suggestion">Suggestion</option>
                          <option value="Question">Question</option>
                          <option value="General Comment">General Comment</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="acp-field">
                        <label className="acp-label">Status</label>
                        <select
                          className="acp-select"
                          value={feedbackFilter}
                          onChange={e => { setFeedbackFilter(e.target.value); setExpandedFeedback(null); }}
                        >
                          <option value="">Active Statuses</option>
                          <option value="New">New</option>
                          <option value="Under Consideration">Under Consideration</option>
                          <option value="In Development">In Development</option>
                          <option value="Production Review">Production Review</option>
                          <option value="Live Review">Live Review</option>
                          <option value="Tabled">Tabled</option>
                          <option value="Resolved">Resolved</option>
                        </select>
                      </div>
                      <div className="acp-field">
                        <label className="acp-label">Read</label>
                        <select
                          className="acp-select"
                          value={feedbackReadFilter}
                          onChange={e => { setFeedbackReadFilter(e.target.value); setExpandedFeedback(null); }}
                        >
                          <option value="">All</option>
                          <option value="unread">Unread</option>
                          <option value="read">Read</option>
                        </select>
                      </div>
                      <div className="acp-field">
                        <label className="acp-label">Priority</label>
                        <select
                          className="acp-select"
                          value={feedbackPriorityFilter}
                          onChange={e => { setFeedbackPriorityFilter(e.target.value); setExpandedFeedback(null); }}
                        >
                          <option value="">All Priorities</option>
                          <option value="Critical">Critical</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>
                      <div className="acp-field">
                        <label className="acp-label">Sort By</label>
                        <select
                          className="acp-select"
                          value={feedbackSort}
                          onChange={e => { setFeedbackSort(e.target.value); setExpandedFeedback(null); }}
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="status">Status</option>
                        </select>
                      </div>
                      {activeCount > 0 && (
                        <button
                          className="af-filter-clear af-filter-clear--panel"
                          onClick={() => { setFeedbackTypeFilter(''); setFeedbackFilter(''); setFeedbackReadFilter(''); setFeedbackPriorityFilter(''); setFeedbackSort('newest'); setExpandedFeedback(null); }}
                        >
                          Clear all
                        </button>
                      )}
                  </div>
                </div>
              );
            })()}

            <div className="af-layout">

              {/* Left: list */}
              <div className="af-list">
                {(() => {
                  const STATUS_ORDER = ['New', 'Under Consideration', 'In Development', 'Production Review', 'Live Review', 'Tabled', 'Resolved'];
                  const filteredFeedback = feedback
                    .filter(f => feedbackFilter ? f.status === feedbackFilter : f.status !== 'Resolved')
                    .filter(f => !feedbackTypeFilter || f.feedback_type === feedbackTypeFilter)
                    .filter(f => !feedbackReadFilter || (feedbackReadFilter === 'unread' ? !f.is_read : f.is_read))
                    .filter(f => !feedbackPriorityFilter || f.priority === feedbackPriorityFilter)
                    .sort((a, b) => {
                      if (feedbackSort === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
                      if (feedbackSort === 'status') {
                        const diff = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
                        return diff !== 0 ? diff : new Date(b.createdAt) - new Date(a.createdAt);
                      }
                      return new Date(b.createdAt) - new Date(a.createdAt);
                    });
                  return filteredFeedback.length === 0 ? (
                    <p className="text-muted small p-3">No feedback found.</p>
                  ) : (
                    <div className="af-list-items">
                      {filteredFeedback.map(f => (
                      <div
                        key={f.id}
                        className={`af-item af-item--${f.status.toLowerCase().replace(/\s+/g, '-')} ${expandedFeedback === f.id ? 'af-item--selected' : ''} ${!f.is_read ? 'af-item--unread' : ''}`}
                        onClick={() => handleFeedbackOpen(f.id)}
                      >
                        <div className="af-item-header">
                          <span className="af-item-subject">
                            {!f.is_read && <span className="af-unread-dot" />}
                            {f.subject}
                          </span>
                          <span className="af-item-time">{new Date(f.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        <div className="af-item-meta">
                          <span>{f.feedback_type}</span>
                          {f.User && <span className="af-item-user">· {f.User.first_name} {f.User.last_name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </div>

              {/* Right: detail panel */}
              <div className="af-detail">
                {expandedFeedback ? (() => {
                  const item = feedback.find(f => f.id === expandedFeedback);
                  if (!item) return null;
                  return (
                    <div className="af-detail-content">
                      <div className="d-flex align-items-start justify-content-between mb-2">
                        <button className="af-close-btn ms-auto" onClick={() => setExpandedFeedback(null)}>
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                      <p className="af-detail-type">{item.feedback_type}</p>
                      <h6 className="af-detail-subject mb-1">{item.subject}</h6>
                      <div className="af-detail-meta-row">
                        <div className="af-detail-meta-left">
                          {item.User && (
                            <>
                              <p className="af-detail-user mb-0">
                                <i className="bi bi-person me-1" />
                                {item.User.first_name} {item.User.last_name}
                              </p>
                              <p className="text-muted mb-0" style={{fontSize:'0.78rem'}}>{item.User.email}</p>
                            </>
                          )}
                        </div>
                        <div className="af-detail-meta-right">
                          <p className="af-detail-meta mb-0">
                            <i className="bi bi-clock me-1" />
                            {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {item.page_url && (
                            <p className="af-detail-meta mb-0">
                              <i className="bi bi-link-45deg me-1" />{item.page_url}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="af-admin-fields">
                        <div className="acp-field">
                          <label className="acp-label">Status</label>
                          <select
                            className={`af-status-select af-status-select--${item.status.toLowerCase().replace(/\s+/g, '-')}`}
                            style={{width: '160px'}}
                            value={item.status}
                            onChange={e => handleFeedbackUpdate(item.id, { status: e.target.value })}
                          >
                            <option value="New">New</option>
                            <option value="Under Consideration">Under Consideration</option>
                            <option value="In Development">In Development</option>
                            <option value="Production Review">Production Review</option>
                            <option value="Live Review">Live Review</option>
                            <option value="Tabled">Tabled</option>
                            <option value="Resolved">Resolved</option>
                          </select>
                        </div>
                        <div className="acp-field">
                          <label className="acp-label">Type</label>
                          <select
                            className="acp-select"
                            style={{width: '150px'}}
                            value={item.admin_type || ''}
                            onChange={e => handleFeedbackUpdate(item.id, { admin_type: e.target.value || null })}
                          >
                            <option value="">— Unassigned —</option>
                            <option value="Major Fix">Major Fix</option>
                            <option value="Minor Fix">Minor Fix</option>
                            <option value="New Feature">New Feature</option>
                            <option value="Enhancement">Enhancement</option>
                            <option value="By Design">By Design</option>
                            <option value="Wont Fix">Wont Fix</option>
                          </select>
                        </div>
                        <div className="acp-field">
                          <label className="acp-label">Priority</label>
                          <select
                            className={`af-priority-select ${item.priority ? `af-priority-select--${item.priority.toLowerCase()}` : ''}`}
                            style={{width: '130px'}}
                            value={item.priority || ''}
                            onChange={e => handleFeedbackUpdate(item.id, { priority: e.target.value || null })}
                          >
                            <option value="">— None —</option>
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                          </select>
                        </div>
                      </div>
                      <div className="af-detail-message">{item.feedback_text}</div>

                      <div className="af-notes">
                        <p className="af-notes-title">Admin Notes</p>
                        {feedbackNotes.length === 0 ? (
                          <p className="af-notes-empty">No notes yet.</p>
                        ) : (
                          <ul className="af-notes-list">
                            {feedbackNotes.map(n => (
                              <li key={n.id} className="af-note">
                                <span className="af-note-time">
                                  {new Date(n.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <p className="af-note-text">{n.note_text}</p>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="af-notes-input">
                          <textarea
                            className="form-control form-control-sm"
                            rows={2}
                            placeholder="Add a note…"
                            value={newNote}
                            onChange={e => setNewNote(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); }}}
                          />
                          <button
                            className="btn btn-sm af-notes-submit"
                            onClick={handleAddNote}
                            disabled={!newNote.trim() || noteSubmitting}
                          >
                            {noteSubmitting ? '…' : 'Add'}
                          </button>
                        </div>
                      </div>

                      <div className="af-notes af-closeout">
                        <p className="af-notes-title">Closeout Note</p>
                        <p className="af-closeout-hint">
                          Sent to the submitter as a notification on its own — the admin notes above are not included.
                        </p>
                        <div className="af-notes-input">
                          <textarea
                            className="form-control form-control-sm"
                            rows={3}
                            placeholder="Summarize the action taken and resolution…"
                            value={closeoutNote}
                            onChange={e => setCloseoutNote(e.target.value)}
                          />
                          <button
                            className="btn btn-sm af-closeout-submit"
                            onClick={handleCloseout}
                            disabled={!closeoutNote.trim() || closeoutSubmitting || !item.user_id}
                            title={!item.user_id ? 'This feedback was submitted anonymously — there is no user to notify.' : undefined}
                          >
                            {closeoutSubmitting ? '…' : (item.closeout_note ? 'Update & Notify' : 'Close Out & Notify')}
                          </button>
                        </div>
                        {item.closeout_note && (
                          <p className="af-closeout-sent">
                            <i className="bi bi-check-circle me-1" />
                            Sent to {item.User ? `${item.User.first_name} ${item.User.last_name}` : 'submitter'}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })() : (
                  <div className="af-detail-empty">
                    <i className="bi bi-chat-square-text" />
                    <p>Select an item to view</p>
                  </div>
                )}
              </div>

            </div>
            </>
          )}
        </div>
      )}

    </div>

    {/* ── Trial period prompt ── */}
    {trialPrompt && (
      <div className="admin-modal-overlay" onClick={closeTrialPrompt}>
        <div className="admin-modal admin-trial-modal" onClick={e => e.stopPropagation()}>
          <div className="admin-modal-header">
            <span className="admin-modal-title">Set Trial Period</span>
            <button className="admin-modal-close" onClick={closeTrialPrompt}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="admin-modal-body admin-trial-body">
            <div className="admin-trial-row">
              <button className="admin-trial-option" onClick={() => confirmTrial(addTrialDays(7))}>7 Days</button>
              <button className="admin-trial-option" onClick={() => confirmTrial(addTrialDays(14))}>14 Days</button>
              <button className="admin-trial-option" onClick={() => confirmTrial(addTrialDays(30))}>30 Days</button>
            </div>
            <div className="admin-trial-row">
              <button className="admin-trial-option" onClick={() => confirmTrial(addTrialMonths(2))}>2 Months</button>
              <button className="admin-trial-option" onClick={() => confirmTrial(addTrialMonths(3))}>3 Months</button>
              <button className="admin-trial-option" onClick={() => confirmTrial(addTrialMonths(6))}>6 Months</button>
              <button className="admin-trial-option" onClick={() => confirmTrial(addTrialMonths(12))}>12 Months</button>
            </div>
            <div className="admin-trial-row">
              <button className="admin-trial-option admin-trial-permanent" onClick={() => confirmTrial(null)}>
                No end date (permanent)
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ── Create organization / division modal ── */}
    {creatingOrg && (
      <AdminCreateOrgModal
        initialMode={creatingOrg.mode}
        presetOrg={creatingOrg.presetOrg || null}
        organizations={organizations}
        groupTypes={allOrgGroupTypes}
        onClose={() => setCreatingOrg(null)}
        onCreated={async () => {
          setCreatingOrg(null);
          await loadOrganizations();
        }}
      />
    )}

    {/* ── Edit organization modal ── */}
    {editingOrg && (
      <EditOrganizationModal
        key={editingOrg.organization_id}
        org_data={editingOrg}
        reRenderOrganization={loadOrganizations}
      />
    )}

    {/* ── User detail modal ── */}
    {(userDetail || loadingDetail) && (
      <div className="admin-modal-overlay" onClick={() => { setUserDetail(null); setUserActivity(null); }}>
        <div className="admin-modal" onClick={e => e.stopPropagation()}>
          <div className="admin-modal-header">
            <span className="admin-modal-title">
              {userDetail ? `${userDetail.first_name} ${userDetail.last_name}` : 'Loading…'}
            </span>
            <button className="admin-modal-close" onClick={() => { setUserDetail(null); setUserActivity(null); }}>
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {loadingDetail && !userDetail ? (
            <div className="admin-modal-body admin-modal-loading">
              <i className="bi bi-arrow-repeat admin-spin" /> Loading…
            </div>
          ) : userDetail && (
            <div className="admin-modal-body">

              {/* Account */}
              <div className="admin-modal-section">
                <div className="admin-modal-section-title">Account</div>
                <div className="admin-modal-row"><span>Email</span><span>{userDetail.email}</span></div>
                <div className="admin-modal-row">
                  <span>Email Confirmed</span>
                  <span>{userDetail.email_confirmed
                    ? <><i className="bi bi-check-circle-fill admin-modal-yes" /> Yes</>
                    : <><i className="bi bi-x-circle-fill admin-modal-no" /> No</>}
                  </span>
                </div>
                <div className="admin-modal-row">
                  <span>Member Since</span>
                  <span>{userDetail.created_at ? new Date(userDetail.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</span>
                </div>
                <div className="admin-modal-row"><span>User ID</span><span className="admin-modal-mono">{userDetail.user_id}</span></div>
                <div className="admin-modal-row"><span>Profile Linked</span><span>{userDetail.profile_id ? <><i className="bi bi-check-circle-fill admin-modal-yes" /> Yes</> : <><i className="bi bi-x-circle-fill admin-modal-no" /> No</>}</span></div>
              </div>

              {/* Subscription */}
              <div className="admin-modal-section">
                <div className="admin-modal-section-title">Subscription</div>
                <div className="admin-modal-row">
                  <span>Individual Access</span>
                  <span>{userDetail.individual_subscription ? <><i className="bi bi-check-circle-fill admin-modal-yes" /> Active</> : <><i className="bi bi-x-circle-fill admin-modal-no" /> None</>}</span>
                </div>
                {userDetail.individual_subscription && (<>
                  <div className="admin-modal-row">
                    <span>Plan</span>
                    <span>{userDetail.subscription_interval ? userDetail.subscription_interval.charAt(0).toUpperCase() + userDetail.subscription_interval.slice(1) : '—'}</span>
                  </div>
                  <div className="admin-modal-row">
                    <span>Source</span>
                    <span>{userDetail.subscription_interval ? 'Self-serve (Lemon Squeezy)' : 'Admin granted'}</span>
                  </div>
                  <div className="admin-modal-row">
                    <span>Started</span>
                    <span>{userDetail.subscription_start ? new Date(userDetail.subscription_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                  </div>
                  <div className="admin-modal-row">
                    <span>Renews / Ends</span>
                    <span>{userDetail.subscription_end ? new Date(userDetail.subscription_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                  </div>
                </>)}
                <div className="admin-modal-row">
                  <span>Workspace Access</span>
                  <span>{userDetail.workplace_subscription ? <><i className="bi bi-check-circle-fill admin-modal-yes" /> Active</> : <><i className="bi bi-x-circle-fill admin-modal-no" /> None</>}</span>
                </div>
                {userDetail.workplace_subscription && userDetail.workplace_subscription_end && (
                  <div className="admin-modal-row">
                    <span>Workspace Access Ends</span>
                    <span>{new Date(userDetail.workplace_subscription_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
                <div className="admin-modal-row">
                  <span>Contributor</span>
                  <span>{userDetail.contributor ? <><i className="bi bi-check-circle-fill admin-modal-yes" /> Yes</> : <><i className="bi bi-x-circle-fill admin-modal-no" /> No</>}</span>
                </div>
              </div>

              {/* Workspaces */}
              {userDetail.TeamMembers?.length > 0 && (
                <div className="admin-modal-section">
                  <div className="admin-modal-section-title">Workspaces</div>
                  {userDetail.TeamMembers.map(m => (
                    <div key={m.team_id} className="admin-modal-row">
                      <span>{m.TeamAccount?.team_name}</span>
                      <span className="admin-modal-role">
                        {m.TeamAccount?.team_admin === userDetail.user_id ? 'Admin' : m.account_manage || m.bill_manage ? 'Manager' : 'Member'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Activity */}
              <div className="admin-modal-section">
                <div className="admin-modal-section-title">Activity</div>
                {!userActivity ? (
                  <div className="admin-modal-row"><span>Last Active</span><span>—</span></div>
                ) : userActivity.last_active === null ? (
                  <div className="admin-modal-row"><span style={{ color: '#a0b0b8', fontSize: '0.8rem' }}>No activity recorded</span></div>
                ) : (<>
                  <div className="admin-modal-row">
                    <span>Last Active</span>
                    <span>{new Date(userActivity.last_active).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                  {userActivity.recent_pages?.length > 0 && (
                    <div className="admin-modal-activity-block">
                      <div className="admin-modal-activity-label">Last 5 Paths</div>
                      {userActivity.recent_pages.map((p, i) => (
                        <div key={i} className="admin-modal-activity-row">
                          <span className="admin-modal-activity-page">{p.path.replace(/^\//, '') || '/'}</span>
                          <span className="admin-modal-activity-time">{new Date(p.visited_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {userActivity.top_pages?.length > 0 && (
                    <div className="admin-modal-activity-block">
                      <div className="admin-modal-activity-label">Top 5 Paths</div>
                      {userActivity.top_pages.map((p, i) => (
                        <div key={i} className="admin-modal-activity-row">
                          <span className="admin-modal-activity-page">{p.path.replace(/^\//, '') || '/'}</span>
                          <span className="admin-modal-activity-count">{p.count} visit{p.count !== 1 ? 's' : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>)}
              </div>

            </div>
          )}
        </div>
      </div>
    )}
    {/* ── Team detail modal ── */}
    {(teamDetail || loadingTeamDetail) && (
      <div className="admin-modal-overlay" onClick={closeTeamDetail}>
        <div className="admin-modal admin-modal--wide" onClick={e => e.stopPropagation()}>
          <div className="admin-modal-header">
            <span className="admin-modal-title">
              {teamDetail ? teamDetail.team_name : 'Loading…'}
            </span>
            <button className="admin-modal-close" onClick={closeTeamDetail}>
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {loadingTeamDetail && !teamDetail ? (
            <div className="admin-modal-body admin-modal-loading">
              <i className="bi bi-arrow-repeat admin-spin" /> Loading…
            </div>
          ) : teamDetail && (
            <div className="admin-modal-body">

              {/* Linked Organization */}
              <div className="admin-modal-section">
                <div className="admin-modal-section-title">Linked Organization</div>
                {teamDetail.Organization ? (
                  <div className="admin-team-org-current">
                    {teamDetail.Organization.logo && (
                      <img src={teamDetail.Organization.logo} alt="" className="admin-team-org-logo" />
                    )}
                    <span className="admin-team-org-name">{teamDetail.Organization.organization_name}</span>
                    <button
                      className="admin-team-org-remove"
                      onClick={() => updateTeamOrg(null)}
                      disabled={savingOrg}
                      title="Remove organization link"
                    >
                      {savingOrg ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Remove'}
                    </button>
                  </div>
                ) : (
                  <p className="admin-team-org-none">No organization linked.</p>
                )}
                <div className="admin-team-search-wrap">
                  <input
                    className="admin-team-search-input"
                    placeholder="Search organizations…"
                    value={orgSearch}
                    onChange={e => handleOrgSearch(e.target.value)}
                  />
                  {orgSearching && <span className="admin-team-search-spinner"><i className="bi bi-arrow-repeat admin-spin" /></span>}
                </div>
                {orgResults.length > 0 && (
                  <div className="admin-team-search-results">
                    {orgResults.map(org => (
                      <button
                        key={org.organization_id}
                        className="admin-team-search-result"
                        onClick={() => updateTeamOrg(org)}
                        disabled={savingOrg}
                      >
                        {org.logo && <img src={org.logo} alt="" className="admin-team-org-logo" />}
                        <span>{org.organization_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Ambassador Program */}
              <div className="admin-modal-section">
                <div className="admin-modal-section-title">Ambassador Program</div>
                <p className="text-muted small mb-2">
                  When enabled, every accepted member of this Workspace gets their own referral code at
                  the rates below, and signups/commissions from any member's code roll up into one pool
                  visible to the Owner and Account Manager on a new Workspace tab.
                </p>
                <div className="admin-trial-row mb-2">
                  <input
                    className="admin-search-input"
                    style={{ maxWidth: 160 }}
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="Monthly rate %"
                    title="Monthly commission rate (%)"
                    value={ambassadorMonthly}
                    onChange={e => setAmbassadorMonthly(e.target.value)}
                  />
                  <input
                    className="admin-search-input"
                    style={{ maxWidth: 160 }}
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="Annual rate %"
                    title="Annual commission rate (%)"
                    value={ambassadorAnnual}
                    onChange={e => setAmbassadorAnnual(e.target.value)}
                  />
                  <button
                    className={`admin-toggle ${teamDetail.ambassador_enabled ? 'on' : 'off'}`}
                    onClick={toggleTeamAmbassador}
                    disabled={savingAmbassador}
                    title={teamDetail.ambassador_enabled ? 'Disable ambassador program' : 'Enable ambassador program'}
                  >
                    {savingAmbassador
                      ? <i className="bi bi-arrow-repeat admin-spin" />
                      : teamDetail.ambassador_enabled
                      ? <i className="bi bi-toggle-on" />
                      : <i className="bi bi-toggle-off" />}
                  </button>
                  {teamDetail.ambassador_enabled && (
                    <button className="awards-save-btn" onClick={saveTeamAmbassadorRate} disabled={savingAmbassador}>
                      Save Rate
                    </button>
                  )}
                </div>
              </div>

              {/* Members */}
              <div className="admin-modal-section">
                <div className="admin-modal-section-title">Members ({teamDetail.TeamMembers?.length ?? 0})</div>
                {teamDetail.TeamMembers?.length === 0 ? (
                  <p className="admin-team-org-none">No members yet.</p>
                ) : (
                  <div className="admin-team-members-list">
                    {teamDetail.TeamMembers?.map(m => (
                      <div key={m.member_id} className="admin-team-member-row">
                        <div className="admin-team-member-info">
                          <span className="admin-team-member-name">
                            {m.UserAccount?.first_name} {m.UserAccount?.last_name}
                          </span>
                          <span className="admin-team-member-email">{m.UserAccount?.email}</span>
                        </div>
                        <span className="admin-modal-role">
                          {teamDetail.team_admin === m.user_id ? 'Admin' : m.account_manage || m.bill_manage ? 'Manager' : 'Member'}
                        </span>
                        {teamDetail.team_admin !== m.user_id && (
                          <button
                            className="admin-team-member-remove"
                            onClick={() => removeTeamMember(m.member_id)}
                            disabled={removingMember === m.member_id}
                            title="Remove member"
                          >
                            {removingMember === m.member_id
                              ? <i className="bi bi-arrow-repeat admin-spin" />
                              : <i className="bi bi-person-dash" />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="admin-modal-section-title" style={{ marginTop: '1rem' }}>Add Member</div>
                <div className="admin-team-search-wrap">
                  <input
                    className="admin-team-search-input"
                    placeholder="Search users by name or email…"
                    value={memberSearch}
                    onChange={e => handleMemberSearch(e.target.value)}
                  />
                  {memberSearching && <span className="admin-team-search-spinner"><i className="bi bi-arrow-repeat admin-spin" /></span>}
                </div>
                {memberResults.length > 0 && (
                  <div className="admin-team-search-results">
                    {memberResults
                      .filter(u => !teamDetail.TeamMembers?.some(m => m.user_id === u.user_id))
                      .map(u => (
                        <button
                          key={u.user_id}
                          className="admin-team-search-result"
                          onClick={() => addTeamMember(u)}
                          disabled={!!addingMember}
                        >
                          <span className="admin-team-member-name">{u.first_name} {u.last_name}</span>
                          <span className="admin-team-member-email">{u.email}</span>
                          {addingMember === u.user_id && <i className="bi bi-arrow-repeat admin-spin ms-auto" />}
                        </button>
                      ))}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}
